import type { InsightCategory } from '@/shared/api/modules/facilities';
import type { IconName } from '@/shared/assets/icons';

type CategoryStyle = {
  label: string;
  /**
   * Glyph beside the title. Positive uses a tick rather than the warning mark —
   * a warning triangle tinted green states the opposite of the card's text.
   */
  glyphIcon: IconName;
  /**
   * Explicit, because the two glyphs have different viewBoxes — `insight-warning`
   * is 20.08 x 19 and would be stretched by a square size.
   */
  glyphSize: { width: number; height: number };
  /** The 4px bar down the card's left edge. */
  accent: string;
  /** Card background. Priority is a gradient; the others are flat. */
  surface: string;
  /** Passed to the shared `Tag`. Medium has no ramp, so it is spelled out. */
  tagClassName: string;
  /** Tint for the category glyph, matching the accent bar. */
  glyph: string;
};

/**
 * Figma nodes 248:20016 (priority), 248:20034 (medium), 248:20056 (positive).
 *
 * Priority is the only card with a gradient — it fades from a red wash at the
 * top into the same surface the other cards use, so a stack of cards still
 * reads as one column.
 *
 * The color scale is sentiment, not severity: Priority and Medium are both
 * warnings and Positive is the only green. The server guarantees the pairing —
 * a block whose text is good news is never delivered as priority or medium.
 */
export const CATEGORY_STYLES: Record<InsightCategory, CategoryStyle> = {
  priority: {
    label: 'Priority',
    glyphIcon: 'insight-warning',
    glyphSize: { width: 20, height: 19 },
    accent: 'bg-insight-priority',
    surface:
      'bg-gradient-to-b from-insight-priority-wash to-insight-surface border border-insight-border',
    tagClassName: 'bg-dust-red-1 text-dust-red-7 inset-ring-dust-red-3',
    glyph: 'text-insight-priority',
  },
  medium: {
    label: 'Medium',
    glyphIcon: 'insight-warning',
    glyphSize: { width: 20, height: 19 },
    accent: 'bg-insight-medium',
    surface: 'bg-insight-surface border border-insight-border',
    tagClassName:
      'bg-insight-medium-wash text-insight-medium inset-ring-insight-medium',
    glyph: 'text-insight-medium',
  },
  positive: {
    label: 'Positive',
    glyphIcon: 'success-circle',
    glyphSize: { width: 20, height: 20 },
    accent: 'bg-polar-green-7',
    surface: 'bg-insight-surface border border-insight-border',
    tagClassName:
      'bg-polar-green-1 text-polar-green-7 inset-ring-polar-green-3',
    glyph: 'text-polar-green-7',
  },
};
